import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTeachers } from '@/hooks/use-public-api';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Users, Star, X, Plus, DollarSign, Globe, BookOpen, Clock } from 'lucide-react';
import { SEO, createBreadcrumbSchema } from '@/components/seo';

const CompareTeachers = () => {
  const { isRTL } = useLanguage();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);

  const { data } = useTeachers({ search: search || undefined, limit: 10 });
  const teachers = (data as any)?.teachers || [];

  const addTeacher = (teacher: any) => {
    if (selected.length < 3 && !selected.find(t => t.name === teacher.name)) {
      setSelected([...selected, teacher]);
      setSearch('');
      setShowSearch(false);
    }
  };

  const removeTeacher = (name: string) => {
    setSelected(selected.filter(t => t.name !== name));
  };

  // Backend returns specialties/languages either as arrays or as
  // comma/newline-separated strings depending on the endpoint.
  const toList = (v: unknown): string[] =>
    Array.isArray(v) ? v.map(String) : typeof v === 'string' ? v.split(/[,\n]/).map(s => s.trim()).filter(Boolean) : [];

  const compareRows = [
    { label: isRTL ? 'التقييم' : 'Rating', icon: Star, getValue: (t: any) => `${t.average_rating || 0}/5 (${t.total_reviews || 0})` },
    { label: isRTL ? 'السعر/ساعة' : 'Price/hr', icon: DollarSign, getValue: (t: any) => `$${t.hourly_rate || 0}` },
    { label: isRTL ? 'التخصصات' : 'Specialties', icon: BookOpen, getValue: (t: any) => toList(t.specialties).join(', ') || '-' },
    { label: isRTL ? 'اللغات' : 'Languages', icon: Globe, getValue: (t: any) => toList(t.languages).join(', ') || '-' },
    { label: isRTL ? 'الخبرة' : 'Experience', icon: Clock, getValue: (t: any) => t.years_of_experience ? `${t.years_of_experience} ${isRTL ? 'سنوات' : 'years'}` : '-' },
    { label: isRTL ? 'الجلسات' : 'Sessions', icon: Users, getValue: (t: any) => `${t.total_sessions || 0}` },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      createBreadcrumbSchema([
        { name: isRTL ? 'الرئيسية' : 'Home', url: 'https://hudanlilmuttaqeen.com/' },
        { name: isRTL ? 'المعلمون' : 'Teachers', url: 'https://hudanlilmuttaqeen.com/teachers' },
        { name: isRTL ? 'مقارنة المعلمين' : 'Compare Teachers', url: 'https://hudanlilmuttaqeen.com/compare-teachers' }
      ])
    ]
  };

  return (
    <Layout>
      <SEO
        title={isRTL ? 'مقارنة المعلمين | هُدًى للمتقين' : 'Compare Teachers | Hudan Lil Muttaqeen'}
        description={isRTL
          ? 'قارن بين معلمي القرآن المعتمدين جنباً إلى جنب من حيث التقييم والسعر والتخصصات والخبرة لاختيار الأنسب لك.'
          : 'Compare certified Quran teachers side by side on rating, price, specialties, and experience to find your perfect match.'}
        structuredData={structuredData}
      />
      <PageHeader
        title="Compare Teachers"
        titleAr="مقارنة المعلمين"
        subtitle="Compare up to 3 teachers side by side"
        subtitleAr="قارن حتى 3 معلمين جنباً إلى جنب"
        size="sm"
      />

      <div className="container py-8 space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Selected Teachers Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {selected.map((teacher, i) => (
            <motion.div
              key={teacher.name}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className="relative">
                <button
                  onClick={() => removeTeacher(teacher.name)}
                  className="absolute top-2 end-2 p-1 rounded-full bg-muted hover:bg-destructive/10 hover:text-destructive transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
                <CardContent className="pt-6 text-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mx-auto flex items-center justify-center overflow-hidden">
                    {teacher.profile_image ? (
                      <img src={teacher.profile_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Users className="h-8 w-8 text-emerald-500" />
                    )}
                  </div>
                  <h3 className="font-semibold text-foreground">{teacher.full_name || teacher.teacher_name}</h3>
                  <div className="flex items-center justify-center gap-1 text-sm">
                    <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                    <span>{teacher.average_rating || 0}</span>
                    <span className="text-muted-foreground">({teacher.total_reviews || 0})</span>
                  </div>
                  <Badge variant="outline">${teacher.hourly_rate || 0}/hr</Badge>
                </CardContent>
              </Card>
            </motion.div>
          ))}

          {/* Add teacher slot */}
          {selected.length < 3 && (
            <Card
              className="border-dashed cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => setShowSearch(true)}
            >
              <CardContent className="pt-6 flex flex-col items-center justify-center min-h-[200px] text-muted-foreground">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                  <Plus className="h-5 w-5" />
                </div>
                <span className="text-sm">{isRTL ? 'إضافة معلم' : 'Add Teacher'}</span>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Search panel */}
        {showSearch && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border bg-card p-4 space-y-3"
          >
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={isRTL ? 'ابحث عن معلم...' : 'Search for a teacher...'}
                  className="ps-9"
                  autoFocus
                />
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setShowSearch(false); setSearch(''); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            {search && teachers.length > 0 && (
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {teachers
                  .filter((t: any) => !selected.find(s => s.name === t.name))
                  .map((teacher: any) => (
                    <button
                      key={teacher.name}
                      onClick={() => addTeacher(teacher)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-start"
                    >
                      <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center overflow-hidden shrink-0">
                        {teacher.profile_image ? (
                          <img src={teacher.profile_image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="h-4 w-4 text-emerald-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{teacher.full_name || teacher.teacher_name}</p>
                        <p className="text-xs text-muted-foreground">${teacher.hourly_rate}/hr</p>
                      </div>
                      <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500 shrink-0" />
                      <span className="text-xs">{teacher.average_rating || 0}</span>
                    </button>
                  ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Comparison Table */}
        {selected.length >= 2 ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border bg-card overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-start p-4 text-sm font-medium text-muted-foreground w-40">
                      {isRTL ? 'المقارنة' : 'Comparison'}
                    </th>
                    {selected.map(t => (
                      <th key={t.name} className="p-4 text-sm font-semibold text-foreground text-center">
                        {t.full_name || t.teacher_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((row, i) => (
                    <tr key={row.label} className={i % 2 === 0 ? 'bg-muted/10' : ''}>
                      <td className="p-4">
                        <span className="flex items-center gap-2 text-sm text-muted-foreground">
                          <row.icon className="h-4 w-4" />
                          {row.label}
                        </span>
                      </td>
                      {selected.map(t => (
                        <td key={t.name} className="p-4 text-center text-sm text-foreground">
                          {row.getValue(t)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : (
          selected.length === 0 && (
            <EmptyState
              icon={Users}
              title={isRTL ? 'ابدأ المقارنة' : 'Start Comparing'}
              description={isRTL ? 'أضف معلمين على الأقل لبدء المقارنة' : 'Add at least 2 teachers to start comparing'}
            />
          )
        )}
      </div>
    </Layout>
  );
};

export default CompareTeachers;
